import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Briefcase, BarChart2 } from 'lucide-react';

/* ── Stat box (4-in-a-row row) ─────────────────────────────────────────────── */
const StatBox = ({ icon: Icon, value, label }) => (
  <div style={{
    flex: '1 1 0',
    backgroundColor: '#f5f5f5',
    border: '1px solid rgba(0,0,0,0.07)',
    borderRadius: '10px',
    padding: '14px 10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
  }}>
    <Icon size={16} color="#E8A020" />
    <span style={{
      fontFamily: 'DM Sans, sans-serif',
      fontSize: '14px', fontWeight: 800,
      color: '#111111', textAlign: 'center', lineHeight: 1.2,
    }}>
      {value}
    </span>
    <span style={{
      fontFamily: 'DM Sans, sans-serif',
      fontSize: '9px', fontWeight: 700,
      color: 'rgba(0,0,0,0.38)',
      textTransform: 'uppercase', letterSpacing: '0.09em', textAlign: 'center',
    }}>
      {label}
    </span>
  </div>
);

/* ── Single career card ─────────────────────────────────────────────────────── */
const CareerCard = ({ role, index }) => {
  if (!role) return null;
  const {
    title, match_percent, why, about,
    salary_range, seniority, entry_difficulty, demand,
    top_companies = [], trajectory = [], skills = [],
  } = role;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.06 }}
      style={{
        backgroundColor: '#ffffff',
        border: '1.5px solid rgba(0,0,0,0.09)',
        borderRadius: '14px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      {/* Top amber accent bar */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #E8A020, #F0B840)' }} />

      <div style={{ padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>

        {/* Title + Match badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
          <h3 style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '18px', fontWeight: 800,
            color: '#111111', lineHeight: 1.15,
            letterSpacing: '-0.02em', flex: 1, minWidth: 0,
          }}>
            {title}
          </h3>
          <div style={{
            backgroundColor: '#E8A020', color: '#ffffff',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px', fontWeight: 800,
            padding: '4px 10px', borderRadius: '9999px',
            whiteSpace: 'nowrap', flexShrink: 0,
            letterSpacing: '0.04em',
          }}>
            {match_percent}% MATCH
          </div>
        </div>

        {/* About this role */}
        <div style={{
          backgroundColor: '#fafafa',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: '8px',
          padding: '12px 14px',
        }}>
          <p style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '10px', fontWeight: 700,
            color: 'rgba(0,0,0,0.35)',
            textTransform: 'uppercase', letterSpacing: '0.10em',
            marginBottom: '6px',
          }}>
            About this role
          </p>
          <p style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px', color: 'rgba(0,0,0,0.65)',
            lineHeight: 1.6, margin: 0,
          }}>
            {about}
          </p>
          {why && (
            <p style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14.5px', color: '#C47A00',
              lineHeight: 1.5, margin: '8px 0 0',
              fontStyle: 'italic',
            }}>
              Why this fits you: {why}
            </p>
          )}
        </div>

        {/* 3 Stat boxes (no difficulty) */}
        <div style={{ display: 'flex', gap: '7px' }}>
          <StatBox icon={TrendingUp} value={salary_range}  label="Salary" />
          <StatBox icon={Briefcase}  value={seniority}     label="Seniority" />
          <StatBox icon={BarChart2}  value={demand}         label="Demand" />
        </div>

        {/* Top companies hiring */}
        {top_companies.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <rect x="1" y="4" width="10" height="7" rx="1" stroke="#E8A020" strokeWidth="1.3" />
                <path d="M4 4V3a2 2 0 014 0v1" stroke="#E8A020" strokeWidth="1.3" />
              </svg>
              <span style={{ fontFamily: 'DM Sans', fontSize: '10px', fontWeight: 700, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.09em' }}>
                Top companies hiring
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {top_companies.slice(0, 7).map((co, i) => (
                <span key={i} style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14px', fontWeight: 500,
                  color: '#111111',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid rgba(0,0,0,0.09)',
                  borderRadius: '9999px',
                  padding: '3px 9px',
                }}>
                  {co}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Growth trajectory */}
        {trajectory.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 9L4 6L6.5 8.5L11 3" stroke="#E8A020" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontFamily: 'DM Sans', fontSize: '10px', fontWeight: 700, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.09em' }}>
                Growth trajectory
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
              {trajectory.map((step, i) => (
                <React.Fragment key={i}>
                  <span style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '14px', fontWeight: i === 0 ? 800 : 500,
                    color: i === 0 ? '#ffffff' : '#111111',
                    backgroundColor: i === 0 ? '#E8A020' : '#f0f0f0',
                    border: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.09)',
                    borderRadius: '6px',
                    padding: '4px 9px',
                    whiteSpace: 'nowrap',
                  }}>
                    {step}
                  </span>
                  {i < trajectory.length - 1 && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 7h8M8 4l3 3-3 3" stroke="rgba(0,0,0,0.25)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Skills to develop */}
        {skills.length > 0 && (
          <div>
            <p style={{ fontFamily: 'DM Sans', fontSize: '10px', fontWeight: 700, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: '8px' }}>
              Skills to develop
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {skills.slice(0, 4).map((skill, i) => (
                <span key={i} style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14px', fontWeight: 600,
                  color: '#C47A00',
                  backgroundColor: 'rgba(232,160,32,0.08)',
                  border: '1px solid rgba(232,160,32,0.20)',
                  borderRadius: '9999px',
                  padding: '3px 9px',
                }}>
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ── Career Paths section ───────────────────────────────────────────────────── */
export const CareerPaths = ({ careerPaths = [] }) => {
  if (!careerPaths || careerPaths.length === 0) return null;

  // Only show ≥60% match (should already be filtered but just in case)
  const visible = careerPaths.filter(r => r.match_percent >= 60).slice(0, 6);
  if (visible.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{ marginBottom: '28px' }}
    >
      {/* Section header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '22px', fontWeight: 700,
          color: '#111111', marginBottom: '4px',
          letterSpacing: '-0.01em',
        }}>
          Career paths{' '}
          <span style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontStyle: 'italic', fontWeight: 500, color: '#E8A020',
          }}>
            for you
          </span>
        </h2>
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '14px', color: 'rgba(0,0,0,0.44)',
        }}>
          {visible.length} roles matching ≥60% of your profile — with salary, companies, and growth trajectory.
        </p>
      </div>

      {/* 3×2 desktop / 2×3 mobile grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
      }}
        className="career-paths-grid"
      >
        {visible.map((role, i) => (
          <CareerCard key={role.role_id || i} role={role} index={i} />
        ))}
      </div>
    </motion.div>
  );
};
